/**
 * Notifications Manager - Alerts & Push Notifications
 * Handles temperature alerts, milestones, and user notifications
 */

class NotificationsManager {
    constructor() {
        this.enabled = true;
        this.soundEnabled = true;
        this.thresholds = {
            tempHigh: 230,
            tempLow: 0,
            rorHigh: 20,
            rorLow: -5
        };
        this.audioContext = null;
        this.permission = 'default';
    }

    /**
     * Initialize notifications
     */
    async init() {
        // Request notification permission
        if ('Notification' in window) {
            this.permission = await Notification.requestPermission();
            console.log('Notification permission:', this.permission);
        }

        // Initialize audio context
        try {
            this.audioContext = new (window.AudioContext || window.webkitAudioContext)();
        } catch (error) {
            console.warn('Audio context not supported:', error);
        }
    }

    /**
     * Check temperature thresholds
     */
    checkThresholds(bt, et, ror) {
        const alerts = [];

        if (this.enabled) {
            // High temperature alert
            if (bt >= this.thresholds.tempHigh) {
                alerts.push({
                    type: 'warning',
                    title: 'High Temperature Alert',
                    message: `Bean temp reached ${bt.toFixed(1)}°C`,
                    icon: '🔥'
                });
            }

            // RoR alerts
            if (ror !== null) {
                if (ror >= this.thresholds.rorHigh) {
                    alerts.push({
                        type: 'warning',
                        title: 'High Rate of Rise',
                        message: `RoR is ${ror.toFixed(1)}°C/min`,
                        icon: '⚠️'
                    });
                }

                if (ror <= this.thresholds.rorLow) {
                    alerts.push({
                        type: 'info',
                        title: 'Negative Rate of Rise',
                        message: `RoR is ${ror.toFixed(1)}°C/min - cooling detected`,
                        icon: '❄️'
                    });
                }
            }
        }

        // Show alerts
        alerts.forEach(alert => this.showAlert(alert));
    }

    /**
     * Show alert notification
     */
    showAlert(alert) {
        // Show toast notification in app
        this.showToast(alert);

        // Show browser notification
        if (this.permission === 'granted') {
            this.showBrowserNotification(alert);
        }

        // Play sound
        if (this.soundEnabled && alert.type === 'warning') {
            this.playAlertSound();
        }

        // Vibrate (mobile)
        if ('vibrate' in navigator && alert.type === 'warning') {
            navigator.vibrate([200, 100, 200]);
        }
    }

    /**
     * Show toast notification in app
     */
    showToast(alert) {
        // Check if toast container exists
        let container = document.getElementById('toastContainer');
        if (!container) {
            container = document.createElement('div');
            container.id = 'toastContainer';
            container.style.cssText = `
                position: fixed;
                top: 80px;
                right: 20px;
                z-index: 2000;
                display: flex;
                flex-direction: column;
                gap: 10px;
                max-width: 400px;
            `;
            document.body.appendChild(container);
        }

        // Create toast element
        const toast = document.createElement('div');
        toast.className = 'toast';

        const bgColor = alert.type === 'warning' ? '#fbbf24' :
            alert.type === 'error' ? '#ef4444' :
                alert.type === 'success' ? '#4ade80' :
                    '#3b82f6';

        toast.style.cssText = `
            background: rgba(26, 26, 26, 0.95);
            border-left: 4px solid ${bgColor};
            border-radius: 8px;
            padding: 16px;
            box-shadow: 0 10px 25px rgba(0, 0, 0, 0.6);
            animation: slideIn 0.3s ease;
            backdrop-filter: blur(10px);
        `;

        toast.innerHTML = `
            <div style="display: flex; align-items: start; gap: 12px;">
                <div style="font-size: 24px;">${alert.icon || '🔔'}</div>
                <div style="flex: 1;">
                    <div style="font-weight: 600; margin-bottom: 4px; color: #ffffff;">
                        ${alert.title}
                    </div>
                    <div style="font-size: 14px; color: #b0b0b0;">
                        ${alert.message}
                    </div>
                </div>
                <button class="toast-close" style="
                    background: transparent;
                    border: none;
                    color: #b0b0b0;
                    cursor: pointer;
                    font-size: 20px;
                    padding: 0;
                    width: 24px;
                    height: 24px;
                ">&times;</button>
            </div>
        `;

        // Add close handler
        toast.querySelector('.toast-close').addEventListener('click', () => {
            toast.remove();
        });

        // Add to container
        container.appendChild(toast);

        // Auto remove after 5 seconds
        setTimeout(() => {
            if (toast.parentElement) {
                toast.style.opacity = '0';
                toast.style.transform = 'translateX(100%)';
                setTimeout(() => toast.remove(), 300);
            }
        }, 5000);

        // Add slide-in animation
        const style = document.createElement('style');
        style.textContent = `
            @keyframes slideIn {
                from { opacity: 0; transform: translateX(100%); }
                to { opacity: 1; transform: translateX(0); }
            }
        `;
        if (!document.querySelector('style[data-toast-animations]')) {
            style.setAttribute('data-toast-animations', '');
            document.head.appendChild(style);
        }
    }

    /**
     * Show browser notification
     */
    showBrowserNotification(alert) {
        if (this.permission !== 'granted') return;

        const notification = new Notification(alert.title, {
            body: alert.message,
            icon: '/assets/icons/icon-192.png',
            badge: '/assets/icons/icon-192.png',
            tag: 'roaster-alert',
            requireInteraction: alert.type === 'warning'
        });

        // Auto close after 5 seconds (unless it requires interaction)
        if (alert.type !== 'warning') {
            setTimeout(() => notification.close(), 5000);
        }
    }

    /**
     * Play alert sound
     */
    playAlertSound(frequency = 800, duration = 200) {
        if (!this.audioContext) return;

        try {
            const oscillator = this.audioContext.createOscillator();
            const gainNode = this.audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(this.audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = 'sine';

            gainNode.gain.setValueAtTime(0.3, this.audioContext.currentTime);
            gainNode.gain.exponentialRampToValueAtTime(
                0.01,
                this.audioContext.currentTime + duration / 1000
            );

            oscillator.start(this.audioContext.currentTime);
            oscillator.stop(this.audioContext.currentTime + duration / 1000);
        } catch (error) {
            console.warn('Failed to play sound:', error);
        }
    }

    /**
     * Notify milestone
     */
    notifyMilestone(milestone, data = {}) {
        const messages = {
            'first-crack': {
                title: '🎯 First Crack Detected!',
                message: `BT: ${data.bt?.toFixed(1)}°C at ${data.time}`,
                icon: '💥'
            },
            'second-crack': {
                title: '🎯 Second Crack!',
                message: `BT: ${data.bt?.toFixed(1)}°C at ${data.time}`,
                icon: '💥💥'
            },
            'development': {
                title: '📈 Development Phase',
                message: `Starting development phase`,
                icon: '📈'
            },
            'custom': {
                title: '🏁 Milestone',
                message: data.message || 'Custom milestone marked',
                icon: '🏁'
            }
        };

        const alert = {
            type: 'success',
            ...messages[milestone]
        };

        this.showAlert(alert);

        // Play different sound for milestones
        if (this.soundEnabled) {
            this.playMilestoneSound();
        }
    }

    /**
     * Play milestone sound (different from alert)
     */
    playMilestoneSound() {
        this.playAlertSound(600, 150);
        setTimeout(() => this.playAlertSound(800, 150), 150);
    }

    /**
     * Update settings
     */
    updateSettings(settings) {
        if (settings.enableAlerts !== undefined) {
            this.enabled = settings.enableAlerts;
        }
        if (settings.enableSound !== undefined) {
            this.soundEnabled = settings.enableSound;
        }
        if (settings.tempHigh !== undefined) {
            this.thresholds.tempHigh = settings.tempHigh;
        }
    }

    /**
     * Test notification
     */
    test() {
        this.showAlert({
            type: 'info',
            title: 'Test Notification',
            message: 'Notifications are working correctly!',
            icon: '✅'
        });
    }
}

export default NotificationsManager;
